import { Module } from 'vuex';
import {
  UserLeague,
  RootState,
  Competitor,
  LeagueEndstate,
  Cohort,
  Nullable,
  Level,
  LeagueCountdowns,
  LeaguesOptOutOrLockedDetails,
} from 'frontend/types';
import { apiRouter } from 'frontend/router';
import { getData, postData } from 'frontend/utils/ajax';

const leagueLeaderboard: Module<UserLeague, RootState> = {
  namespaced: true,
  state: {
    enable_leagues: false,
    has_unlocked_leagues: false,
    welcome_to_leagues_dismissed: false,
    optOutLockedDetails: null,
    cohort: null,
    endstate: null,
    competitor: null,
    current_level: null,
    advancement_message: null,
    levels: [],
    show_leaderboard_in_lesson_endstate: false,
    xp_needed_to_join_leagues: 0,
    countdowns: {
      seconds_left_in_week: 0,
      seconds_left_to_join: 0,
    },
  },
  getters: {
    enableLeagues: (state: UserLeague): boolean => state.enable_leagues,
    hasUnlockedLeagues: (state: UserLeague): boolean => state.has_unlocked_leagues,
    welcomeToLeaguesDismissed: (state: UserLeague): boolean => state.welcome_to_leagues_dismissed,
    optOutLockedDetails: (state: UserLeague): Nullable<LeaguesOptOutOrLockedDetails> =>
      state.optOutLockedDetails,
    cohort: (state: UserLeague): Nullable<Cohort> => state.cohort,
    endstate: (state: UserLeague): Nullable<LeagueEndstate> => state.endstate,
    competitor: (state: UserLeague): Nullable<Competitor> => state.competitor,
    currentLevel: (state: UserLeague): Nullable<Level> => state.current_level,
    advancementMessage: (state: UserLeague): Nullable<string> => state.advancement_message,
    levels: (state: UserLeague): Level[] => state.levels,
    userLeague: (state: UserLeague): UserLeague => state,
    cohortIsIncomplete: (state: UserLeague): boolean => {
      if (state.cohort === null) {
        return true;
      }

      const { competitors, max_competitors_in_cohort } = state.cohort;

      return competitors.length < max_competitors_in_cohort;
    },
    showLeaderboardInLessonEndstate: (state: UserLeague): boolean =>
      state.show_leaderboard_in_lesson_endstate,
    countdowns: (state: UserLeague): LeagueCountdowns => state.countdowns,
    lastRankToAdvance: (state: UserLeague): Nullable<number> => {
      if (state.cohort) {
        return state.cohort.last_rank_to_advance;
      }

      throw new Error('Cannot get last_rank_to_advance for uncohorted user.');
    },
    firstRankToDropBack: (state: UserLeague): Nullable<number> => {
      if (state.cohort) {
        return state.cohort.first_rank_to_drop_back;
      }

      throw new Error('Cannot get first_rank_to_drop_back for uncohorted user.');
    },
    xpNeededToJoinLeagues: (state: UserLeague): number => state.xp_needed_to_join_leagues,
  },
  mutations: {
    setValue(state: UserLeague, value: UserLeague): void {
      if (!value) {
        // Rather than wipe the leaderboard data in this store. just ensure it doesn't show in
        // the lesson endstate.
        // The leaderboard will be repopulated when there's fresh data to show.
        state.show_leaderboard_in_lesson_endstate = false;
      } else {
        Object.assign(state, value);
      }
    },
    setWelcomeToLeaguesDismissed(state: UserLeague, value: boolean): void {
      state.welcome_to_leagues_dismissed = value;
    },
    setOptOutOrLockedDetails(state: UserLeague, value: LeaguesOptOutOrLockedDetails): void {
      state.optOutLockedDetails = value;
    },
    optimisticallyClearEndstate(state: UserLeague): void {
      state.endstate = null;
    },
  },
  actions: {
    welcomeToLeaguesDismissed({ commit }): void {
      /**
       * NOTE: There is the edge case that when a user clicks Continue,
       * we're already into the next Leagues week. In that case they'll see
       * a stale leaderboard from the Vuex store.
       * Probably not a big deal since a refresh will fix the state.
       */
      const url = apiRouter().resolve({
        params: {},
        name: 'welcome_to_leagues_dismissed',
      });

      postData(url.href);
      commit('setWelcomeToLeaguesDismissed', true);
    },
    async dismissLeaguesEOW({ commit }): Promise<void> {
      const url = apiRouter().resolve({
        params: {},
        name: 'leagues_viewed_endstate',
      });

      try {
        commit('optimisticallyClearEndstate');
        await postData(url.href);
      } catch {
        return;
      }
    },
    async refreshCurrentLeagueData({ commit }): Promise<void> {
      const url = apiRouter().resolve({
        params: {},
        name: 'current_league',
      });

      try {
        const currentLeagueData = await getData(url.href);
        commit('setValue', currentLeagueData);
      } catch {
        return;
      }
    },
  },
};

export default leagueLeaderboard;
